import { UzInput } from '@utazzo/ui';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';

/**
 * Multi-select for a reason set curated onto a call sub-status. Shared by
 * the follow-up reason and lost reason mappings (ADR-0067) so the two
 * behave identically.
 *
 * Empty selection is a MEANINGFUL state, not an unfinished one: it means
 * "unrestricted", and the lead-flow dropdown falls back to every active
 * reason. The footer says so explicitly, because an operator seeing zero
 * checked boxes would otherwise assume the dropdown will come up empty.
 */
export const ReasonsPicker = ({
  label,
  description,
  searchPlaceholder,
  emptyResultsLabel,
  unrestrictedLabel,
  value,
  onChange,
  options,
  disabled,
}: {
  label: string;
  description: string;
  searchPlaceholder: string;
  emptyResultsLabel: string;
  unrestrictedLabel: string;
  value: ReadonlyArray<string>;
  onChange: (ids: string[]) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  disabled?: boolean;
}): React.JSX.Element => {
  const [search, setSearch] = useState('');

  const selectedSet = useMemo(() => new Set(value), [value]);
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return options.filter((o) => !term || o.label.toLowerCase().includes(term));
  }, [options, search]);

  const toggle = (id: string): void => {
    if (disabled) return;
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  const toggleAllVisible = (): void => {
    if (disabled) return;
    const allSelected = rows.length > 0 && rows.every((r) => selectedSet.has(r.value));
    const next = new Set(selectedSet);
    for (const r of rows) {
      if (allSelected) next.delete(r.value);
      else next.add(r.value);
    }
    onChange(Array.from(next));
  };

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <label className="text-sm font-medium text-zinc-700">{label}</label>
        <p className="text-xs text-zinc-500">{description}</p>
      </div>
      <div className="relative">
        <Search className="absolute top-2.5 left-2 h-3.5 w-3.5 text-zinc-400" />
        <UzInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className="h-9 pl-8 text-sm"
          disabled={disabled}
        />
      </div>
      <div className="max-h-52 overflow-y-auto rounded-md border border-zinc-200 bg-zinc-50 p-2">
        {rows.length === 0 ? (
          <p className="py-4 text-center text-xs text-zinc-500">{emptyResultsLabel}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {rows.map((row) => (
              <li key={row.value}>
                <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-white">
                  <input
                    type="checkbox"
                    checked={selectedSet.has(row.value)}
                    disabled={disabled}
                    onChange={() => toggle(row.value)}
                  />
                  <span className="truncate">{row.label}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
        {rows.length > 0 && (
          <div className="mt-2 flex items-center justify-between border-t border-zinc-200 pt-2">
            <label className="flex items-center gap-2 text-xs text-zinc-500">
              <input
                type="checkbox"
                checked={rows.length > 0 && rows.every((r) => selectedSet.has(r.value))}
                disabled={disabled}
                onChange={toggleAllVisible}
              />
              Select all visible
            </label>
            <span className="text-xs text-zinc-500">
              {selectedSet.size === 0 ? unrestrictedLabel : `${selectedSet.size} selected`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
